// Room Build-Up — element vocabulary and per-room quantity resolution
// (2026-09-11 framework doc; db/70_room_elements.sql). An element is a
// catalog Item/Assembly the estimator has added to a room; this module
// says how its quantity is determined and formats the "basis" line the
// Room Build-Up card shows. Pure functions, no React/DB.
//
//   Room -> Element -> Engine / rule -> Effective quantity -> (placement) -> Estimate
//
// Presence is manual, quantity can be automatic: nothing here runs for a
// room unless an element exists for it.

import { calcAreas } from "../utils/takeoffMath";
import { polygonOf, polygonArea, polygonPerimeter, roomVertices } from "../utils/takeoffGeometry";
import { netScopeArea, backOutsForElement, isRing, BACK_OUT_BASES } from "../utils/takeoffExclusions";
import { openingsForRoom, openingAreaSf, openingWidthFt, openingHeightFt, resolveOpeningDeductionPolicy } from "./openings";
import { computeRoomFormulaQty, explainRoomFormulaFailure } from "../utils/roomFormulaEngine";
import { effectiveCriterion } from "./takeoffCriteria";

export const ELEMENT_DISCIPLINES = [
  { key: "arch",     label: "Architectural",     icon: "🏗️", trade: "architectural" },
  { key: "mech",     label: "Mechanical / HVAC", icon: "🌀", trade: "mechanical" },
  { key: "elec",     label: "Electrical",        icon: "⚡", trade: "electrical" },
  { key: "process",  label: "Process Utilities", icon: "🧪", trade: null },
  { key: "controls", label: "Controls",          icon: "🎛️", trade: null },
];
export const DISCIPLINE_BY_KEY = Object.fromEntries(ELEMENT_DISCIPLINES.map(d => [d.key, d]));

// How an element's quantity is determined. `method` is the default
// QuantityMethod a new element of that basis gets.
export const ELEMENT_QUANTITY_BASES = [
  { value: "floor_area",   label: "Floor area (SF)",                        method: "calculated", unit: "sf" },
  { value: "ceiling_area", label: "Ceiling area (SF)",                      method: "calculated", unit: "sf" },
  { value: "perimeter",    label: "Room perimeter (LF)",                    method: "calculated", unit: "lf" },
  { value: "wall_area",    label: "Wall area — perimeter × height (SF)",    method: "calculated", unit: "sf" },
  { value: "engine_hepa",  label: "HEPA engine (count from airflow / coverage)", method: "calculated", unit: "ea" },
  { value: "engine_light", label: "Lighting engine (count from fc target)", method: "calculated", unit: "ea" },
  { value: "placed",       label: "Placed on the canvas (count of symbols)", method: "placed", unit: "ea" },
  { value: "fixed",        label: "Fixed quantity per room",                method: "fixed", unit: "ea" },
];
export const BASIS_BY_VALUE = Object.fromEntries(ELEMENT_QUANTITY_BASES.map(b => [b.value, b]));

// The entity's explicit ElementQuantityBasis wins; otherwise infer from the
// classifications the catalog already carries (AutoSystem = an engine,
// Style='count' = placeable, ScopeQuantityBasis from the scope-placement
// work), else a fixed quantity.
export function inferElementBasis(entity) {
  if (!entity) return "fixed";
  if (entity.ElementQuantityBasis && BASIS_BY_VALUE[entity.ElementQuantityBasis]) return entity.ElementQuantityBasis;
  if (entity.AutoSystem === "hepa") return "engine_hepa";
  if (entity.AutoSystem === "light") return "engine_light";
  if (entity.Style === "count") return "placed";
  if (entity.ScopeQuantityBasis === "perimeterLF") return "perimeter";
  if (entity.ScopeQuantityBasis === "wallSF") return "wall_area";
  return "fixed";
}

// The one rule for what an "+ Add Element" picker offers, shared by the Room
// Detail Flyout and the Room Template editor so the two cannot drift: the
// curated list (rows flagged "Room element" whose discipline matches the
// group) by default, or the WHOLE catalog when `showAll` is set — or when
// nothing is flagged yet. Any Item / Assembly can be a room's element; the
// flag only curates the short list.
export function elementPickerPool(entities, disciplineKey, { showAll = false } = {}) {
  const list = entities || [];
  if (showAll || !list.some(e => e.IsRoomElement)) return list;
  return list.filter(e => e.IsRoomElement && (inferElementDiscipline(e) || disciplineKey) === disciplineKey);
}

const TRADE_TO_DISCIPLINE = { architectural: "arch", mechanical: "mech", electrical: "elec" };
export function inferElementDiscipline(entity) {
  if (!entity) return null;
  if (entity.ElementDiscipline && DISCIPLINE_BY_KEY[entity.ElementDiscipline]) return entity.ElementDiscipline;
  if (entity.AutoSystem === "hepa") return "mech";
  if (entity.AutoSystem === "light") return "elec";
  return TRADE_TO_DISCIPLINE[String(entity.Trade || "").toLowerCase()] || null;
}

// Discipline Criteria keys that are really per-room QUANTITIES, not design
// criteria — they predate Room Build-Up. The template editor demotes them to
// a "legacy quantity defaults" section (prefer a Standard Element with a
// fixed qty); they are still read by the electrical circuit engine
// (outlet_count), the derived scope formulas (doors/windows/pass-throughs/
// drops) and as the lighting fallback (lighting_fixture_count) when a fixture
// has no lumen data, so they stay editable until those consumers migrate.
export const LEGACY_COUNT_CRITERIA = new Set([
  "door_count", "window_count", "pass_throughs", "outlet_count", "lighting_fixture_count",
  "hepa_count_override", "cda_drops", "n2_drops", "vac_drops", "pw_drops",
]);

// Architectural criteria that are really SCOPE choices typed as a dropdown of
// hard-coded option text ("Walkable panel", "Epoxy w/ integral cove"): they
// correlate to no catalog Item / Assembly, so they never priced and fed only
// the derived scope list. The Room Template editor no longer offers them as
// design criteria — the template's Standard Elements (and, for walls, its wall
// system) say the same thing against the real catalog. Stored values stay
// readable until converted (utils/legacyScopeConversion.js).
export const LEGACY_SCOPE_SELECTS = new Set(["wall_system", "ceiling_system", "floor_finish", "coving"]);

// Which of the derived architectural scope lines (roomScopeFormulas.js
// sourceKeys) a room's REAL elements already cover — those lines are hidden in
// the flyout so the same scope is never listed twice (one from criteria, one
// priced). Geometry bases speak for themselves; a count line is covered by an
// element tagged with its legacy criterion field, else by a matching name.
const COVERED_BY_COUNT_FIELD = { door_count: "door_ea", window_count: "window_ea", pass_throughs: "passthrough_ea" };
const COUNT_NAME_WORDS = { door_ea: /door/i, window_ea: /window|vision/i, passthrough_ea: /pass[- ]?thru|pass[- ]?through/i };
export function coveredBaselineKeys(elements = []) {
  const covered = new Set();
  for (const e of elements) {
    const basis = inferElementBasis(e);
    const name = e.Name || e.EntityName || e.ItemName || e.Description || "";
    if (basis === "ceiling_area") covered.add("ceiling_sf");
    if (basis === "floor_area") covered.add("floor_sf");
    if (basis === "perimeter" && /cov/i.test(name)) covered.add("coving_lf");
    if (COVERED_BY_COUNT_FIELD[e.SyncsToCriteriaField]) covered.add(COVERED_BY_COUNT_FIELD[e.SyncsToCriteriaField]);
    else if (!/labor|install/i.test(name)) for (const [key, re] of Object.entries(COUNT_NAME_WORDS)) if (re.test(name)) covered.add(key);
  }
  return covered;
}

// P0 Phase D consumer map (2026-09-12) — every reader and writer of each
// legacy count, and what replaces it. The narrative lives in
// docs/implementation/P0_LEGACY_COUNT_CONSUMERS.md; src/test/
// legacyCountCriteria.test.js pins this table to the real consumers so a
// field can't be retired while something still reads it. `role`:
//   'quantity'        — a per-room count Room Build-Up supersedes
//   'engine_override' — the HEPA/lighting engines' ONE override store (kept
//                       on purpose — see db/70; never move into OverrideQty)
export const LEGACY_COUNT_CONSUMERS = {
  door_count:    { role: "quantity", discipline: "arch",
    readBy: ["derived architectural scope list (roomScopeFormulas.js)", "room formulas as doorCount (roomFormulaEngine.js)", "canvas door glyph fallback (TakeoffCanvas.jsx)"],
    writtenBy: ["Design criteria per-room input", "Room Template legacy defaults (db/55)", "canvas symbol write-through sync (SyncsToCriteriaField, sp_SaveTakeoffConditionDrawn)"],
    replacement: "a placeable door Room Element counted from the canvas; stable door identity is P1" },
  window_count:  { role: "quantity", discipline: "arch",
    readBy: ["derived architectural scope list (roomScopeFormulas.js)", "room formulas as windowCount (roomFormulaEngine.js)", "canvas window glyph fallback (TakeoffCanvas.jsx)"],
    writtenBy: ["Design criteria per-room input", "Room Template legacy defaults (db/55)", "canvas symbol write-through sync (SyncsToCriteriaField)"],
    replacement: "a placeable window Room Element counted from the canvas; stable identity is P1" },
  pass_throughs: { role: "quantity", discipline: "arch",
    readBy: ["derived architectural scope list (roomScopeFormulas.js)", "room formulas as passThroughs (roomFormulaEngine.js)"],
    writtenBy: ["Design criteria per-room input", "Room Template legacy defaults (db/55)", "canvas symbol write-through sync (SyncsToCriteriaField)"],
    replacement: "a placeable pass-through Room Element; stable identity is P1" },
  outlet_count:  { role: "quantity", discipline: "elec",
    readBy: ["receptacle branch circuits (electricalCircuitEngine.js roomDeviceCounts) — FALLBACK only, for a room without a receptacle element", "room formulas as outletCount (roomFormulaEngine.js) — fallback only, same rule (resolveLegacyCount)"],
    writtenBy: ["Design criteria per-room input", "Room Template legacy defaults (db/55)", "canvas symbol write-through sync (SyncsToCriteriaField='outlet_count', db/37)"],
    replacement: "a receptacle Room Element — an Item/Assembly tagged SyncsToCriteriaField='outlet_count' (fixed, placed or formula quantity); the circuit engine and the outletCount formula variable read it since 2026-09-12 (resolveLegacyCount) — the field is now fallback-only for every reader" },
  lighting_fixture_count: { role: "engine_override", discipline: "elec",
    readBy: ["lighting engine override + no-lumen-data fallback (roomSystemsEngine.js)", "persisted engineering row (db/69)"],
    writtenBy: ["Room Build-Up lighting card override", "Design criteria per-room input", "Room Template legacy defaults (db/55, fallback only)"],
    replacement: "none — stays the lighting engine's override store; the template default retires once every fixture option carries lumens" },
  hepa_count_override: { role: "engine_override", discipline: "mech",
    readBy: ["HEPA engine override (roomSystemsEngine.js)", "persisted engineering row (db/69)"],
    writtenBy: ["Room Build-Up HEPA card override", "Design criteria per-room input"],
    replacement: "none — stays the HEPA engine's override store" },
  cda_drops: { role: "quantity", discipline: "process",
    readBy: ["room formulas as cdaDrops (roomFormulaEngine.js) — fallback only, a drop element wins (resolveLegacyCount)", "Utility demand legacy-drop reference list (utilityDemand.js legacyDropsByUtility) — same rule, reference only, never a demand"],
    writtenBy: ["Design criteria per-room input", "Room Template legacy defaults (db/55)", "canvas symbol write-through sync (SyncsToCriteriaField='cda_drops', db/37)"],
    replacement: "a CDA drop Room Element — an Item/Assembly tagged SyncsToCriteriaField='cda_drops' (fixed, placed or formula quantity), read since 2026-09-12; per-drop demand only through an explicit Basis of Design / library assumption (Process Utilities Phase C) — the field is now fallback-only for every reader" },
  n2_drops: { role: "quantity", discipline: "process",
    readBy: ["room formulas as n2Drops (roomFormulaEngine.js) — fallback only, a drop element wins (resolveLegacyCount)", "Utility demand legacy-drop reference list (utilityDemand.js legacyDropsByUtility) — same rule, reference only, never a demand"],
    writtenBy: ["Design criteria per-room input", "Room Template legacy defaults (db/55)", "canvas symbol write-through sync (SyncsToCriteriaField='n2_drops', db/37)"],
    replacement: "an N₂ drop Room Element — an Item/Assembly tagged SyncsToCriteriaField='n2_drops' (fixed, placed or formula quantity), read since 2026-09-12; per-drop demand only through an explicit Basis of Design / library assumption (Process Utilities Phase C) — the field is now fallback-only for every reader" },
  vac_drops: { role: "quantity", discipline: "process",
    readBy: ["room formulas as vacDrops (roomFormulaEngine.js) — fallback only, a drop element wins (resolveLegacyCount)", "Utility demand legacy-drop reference list (utilityDemand.js legacyDropsByUtility) — same rule, reference only, never a demand"],
    writtenBy: ["Design criteria per-room input", "Room Template legacy defaults (db/55)", "canvas symbol write-through sync (SyncsToCriteriaField='vac_drops', db/37)"],
    replacement: "a vacuum drop Room Element — an Item/Assembly tagged SyncsToCriteriaField='vac_drops' (fixed, placed or formula quantity), read since 2026-09-12; per-drop demand only through an explicit Basis of Design / library assumption (Process Utilities Phase C) — the field is now fallback-only for every reader" },
  pw_drops: { role: "quantity", discipline: "process",
    readBy: ["room formulas as pwDrops (roomFormulaEngine.js) — fallback only, a drop element wins (resolveLegacyCount)", "Utility demand legacy-drop reference list (utilityDemand.js legacyDropsByUtility) — same rule, reference only, never a demand"],
    writtenBy: ["Design criteria per-room input", "Room Template legacy defaults (db/55)", "canvas symbol write-through sync (SyncsToCriteriaField='pw_drops', db/37)"],
    replacement: "a PW/DI drop Room Element — an Item/Assembly tagged SyncsToCriteriaField='pw_drops' (fixed, placed or formula quantity), read since 2026-09-12; per-drop demand only through an explicit Basis of Design / library assumption (Process Utilities Phase C) — the field is now fallback-only for every reader" },
};

// A placeable element can be put on the canvas as a symbol.
export const isPlaceable = (entity) => entity?.Style === "count";
// An engine element's override lives on the room's criteria (one override
// store per quantity), not on the element row.
export const isEngineBasis = (basis) => basis === "engine_hepa" || basis === "engine_light";
// An entity whose count the HEPA / lighting engine owns.
export const isEngineEntity = (entity) => isEngineBasis(inferElementBasis(entity));

// Legacy-field replacement (P0 Phase D migration). An Item/Assembly tagged
// SyncsToCriteriaField='<legacy count field>' is the Room Element that
// supersedes that field: while a room carries such an element, a migrated
// consumer reads the element's effective quantity and the legacy criterion is
// only the fallback for rooms without one (old revisions, templates not yet
// carrying the element). The same tag drives the canvas write-through for
// placed symbols (db/37), so a placed element keeps the legacy field mirrored
// for readers that haven't migrated yet. First consumer: receptacle circuits
// (electricalCircuitEngine.js roomDeviceCounts, 'outlet_count').
export const legacyFieldOf = (entity) => entity?.SyncsToCriteriaField || null;
export const elementsReplacingField = (elements, field) => (elements || []).filter(e => legacyFieldOf(e) === field);

// THE rule for a legacy per-room count that has a Room Element replacement
// (P0 Phase D migration; first used for outlet_count → receptacle circuits,
// then the four *_drops → room formulas + Utility demand reference list):
//   the room's elements tagged SyncsToCriteriaField = fieldKey WIN — Σ of their
//   effective quantity (fixed / placed / formula, with the element's own
//   override) through resolveElementQuantity, the same resolver the flyout,
//   Estimate and template editor use;
//   a room WITHOUT one falls back to the criterion chain (own row → room
//   template default → project default), so nothing changes for a revision
//   that has no such elements.
// `ctx` is the systemsCtx ({ roomElementsByRoom, takeoffConditions, formulas,
// … }). A formula-quantity element is resolved with
// ctx.legacyCountsFromCriteria set, so a formula on the element itself (e.g.
// `cdaDrops + 1`) reads the criterion for the field it replaces instead of
// recursing into its own element. Returns { value, source: 'element' |
// 'legacy', elements: [{ roomElementId, code, name, method, effective, warnings }] }.
export function resolveLegacyCount(fieldKey, room, criteria = {}, ctx = {}) {
  const discipline = LEGACY_COUNT_CONSUMERS[fieldKey]?.discipline;
  const fromCriteria = () => ({ value: discipline ? +effectiveCriterion(criteria, discipline, fieldKey, room).value || 0 : 0, source: "legacy", elements: [] });
  if (!discipline || ctx.legacyCountsFromCriteria) return fromCriteria();
  const elements = elementsReplacingField(ctx.roomElementsByRoom?.[room.RoomID], fieldKey);
  if (!elements.length) return fromCriteria();
  const innerCtx = { ...ctx, legacyCountsFromCriteria: true };
  const conditions = ctx.takeoffConditions || [], formulas = ctx.formulas || [];
  const resolved = elements.map((el) => {
    const q = resolveElementQuantity({ element: el, room, systems: null, placed: placedCountFor(el, room.RoomID, conditions), formulas, criteria, ctx: innerCtx });
    return { roomElementId: el.RoomElementID, code: el.Code, name: el.Name, method: q.method, effective: q.effective, warnings: q.warnings };
  });
  const total = resolved.reduce((t, e) => t + (Number.isFinite(e.effective) ? e.effective : 0), 0);
  return { value: Math.max(0, Math.round(total)), source: "element", elements: resolved };
}

// Engine quantity rule (2026-09-12): an engine element is ALWAYS
// 'calculated' — by the engine, or by a formula that consumes the engine's
// outputs. A 'fixed' or 'placed' method on it would be a second override
// store competing with the criterion row (hepa_count_override /
// lighting_fixture_count) while the auto-layout kept generating the engine's
// count, so resolveElementQuantity ignores such a method (with a warning),
// sp_SaveRoomElement rejects it and sp_SeedRoomElementsFromTemplate never
// produces it. The estimator's control is the override on the element card.

// How a Room Template Standard Element row (RoomTemplateEquipment, db/55)
// seeds onto a room. MIRRORS sp_SeedRoomElementsFromTemplate (db/70) — the
// proc is authoritative; this copy lets the template editor show exactly
// what a room will receive and lets the rule be unit-tested. Keep in step.
//   formula        -> 'calculated' + the formula (a fixed qty is dropped)
//   engine entity  -> 'calculated' by the engine (a fixed qty is dropped)
//   fixed qty      -> 'fixed'
//   placeable      -> 'placed'
//   otherwise      -> 'calculated' off the entity's own basis
export function seedQuantityFromTemplateRow(row, entity = row) {
  const formulaId = row?.QuantityFormulaID ?? null;
  const fixed = row?.FixedQtyPerRoom != null && row.FixedQtyPerRoom !== "" && Number.isFinite(+row.FixedQtyPerRoom) ? +row.FixedQtyPerRoom : null;
  const none = { ignoredFixedQty: null, ignoredReason: null };
  if (formulaId) return { method: "calculated", formulaId, fixedQty: null, ignoredFixedQty: fixed, ignoredReason: fixed != null ? "formula" : null };
  if (isEngineEntity(entity)) return { method: "calculated", formulaId: null, fixedQty: null, ignoredFixedQty: fixed, ignoredReason: fixed != null ? "engine" : null };
  if (fixed != null) return { method: "fixed", formulaId: null, fixedQty: fixed, ...none };
  if (inferElementBasis(entity) === "placed") return { method: "placed", formulaId: null, fixedQty: null, ...none };
  return { method: "calculated", formulaId: null, fixedQty: null, ...none };
}

export function entityKeyOf(e) {
  const kind = e.Kind || e.kind;
  const id = kind === "item" ? (e.ItemID ?? e.id) : (e.AssemblyID ?? e.id);
  return `${kind}:${id}`;
}

// Room areas from its real shape when traced, else the bounding box.
export function roomAreasOf(room) {
  const L = +room.LengthFt, W = +room.WidthFt, H = +room.HeightFt || 0;
  if (!(L > 0) || !(W > 0)) return { floorSF: 0, perimeterLF: 0, wallSF: 0, H };
  const poly = polygonOf(room);
  const overrides = poly ? { areaOverrideFt2: polygonArea(poly), perimeterOverrideFt: polygonPerimeter(poly) } : {};
  const a = calcAreas({ length: L, width: W, height: H, ...overrides });
  return { floorSF: a.floorSF, perimeterLF: a.perimeterLF, wallSF: a.perimeterLF * H, H };
}

// Count of an entity's symbols placed in a room (engine-generated + hand-placed).
export function placedCountFor(entity, roomId, takeoffConditions = []) {
  const kind = entity.Kind || entity.kind;
  const id = kind === "item" ? entity.ItemID : entity.AssemblyID;
  return takeoffConditions.filter(c => c.RoomID === roomId && c.Style === "count" && c.Kind === kind
    && (kind === "item" ? c.ItemID === id : c.AssemblyID === id)).length;
}

const r1 = (v) => Math.round(v * 10) / 10;
const fmt0 = (v) => Math.round(v).toLocaleString();

// The EstimateCostFormulas row a 'calculated' element evaluates instead of its
// entity's basis (QuantityFormulaID — seeded from a Room Template Standard
// Element's formula or picked in the flyout). sp_GetRoomElements joins the
// formula's Code/Expression onto the row, so a caller with no formulas list
// still resolves it.
export function formulaForElement(element, formulas = []) {
  const id = element?.QuantityFormulaID;
  if (!id) return null;
  return formulas.find(f => f.FormulaID === id)
    || (element.FormulaExpression ? { FormulaID: id, Code: element.FormulaCode, Expression: element.FormulaExpression, OutputUnit: element.FormulaOutputUnit } : null);
}

// Resolve one element's quantity.
//   element   - RoomElements row (with the entity columns sp_GetRoomElements joins in)
//   room      - TakeoffRooms row
//   systems   - calcRoomSystems() result for the room (already gated on elements)
//   placed    - placedCountFor(...) for this entity in this room
//   formulas  - EstimateCostFormulas rows (optional; the row's own joined
//               formula columns are the fallback), criteria + ctx feed a
//               formula's per-room variables (roomFormulaEngine.js)
//   backOuts  - backOutsForElement(element, takeoffConditions) — the deduct
//               polygons drawn against THIS element (Takeoff Canvas 2.0 D-2);
//               omitted → derived from ctx.takeoffConditions when present
//   openings  - openingsForRoom(room, takeoffConditions, rooms) — the openings
//               in this room's walls, deducted from a wall-area element by
//               policy (D-3); omitted → derived from ctx.takeoffConditions /
//               ctx.rooms when present
// Returns { basis, method, storedMethod, unit, calculated, override, effective,
//           required, placed, basisLabel, warnings, engine, formula, usesFormula,
//           net } — net = { gross, deductions, totalDeduction, overlapArea,
//           calculatedNet } when back-outs apply, else null
//   method       - the method actually applied; storedMethod - the row's own
//                  QuantityMethod (differs only for an engine element carrying
//                  a stale 'fixed'/'placed' — see the engine quantity rule)
export function resolveElementQuantity({ element, room, systems, placed = 0, formulas = [], formula = null, criteria = {}, ctx = {}, backOuts = null, openings = null }) {
  const basis = inferElementBasis(element);
  const storedMethod = element.QuantityMethod || BASIS_BY_VALUE[basis]?.method || "calculated";
  const fml = formula || formulaForElement(element, formulas);
  const warnings = [];
  let method = storedMethod;
  if (isEngineBasis(basis) && method !== "calculated") {
    method = "calculated";
    const fixedNote = storedMethod === "fixed" && element.FixedQty != null ? ` (fixed ${+element.FixedQty})` : "";
    warnings.push({ code: "element_engine_method_ignored", message: `Stored quantity method "${storedMethod}"${fixedNote} is ignored — the ${basis === "engine_hepa" ? "HEPA" : "lighting"} engine governs this count. Enter an override on this card instead.` });
  }
  const usesFormula = method === "calculated" && !!fml;
  const engineBasis = isEngineBasis(basis) && !usesFormula;
  const unit = (element.UOM || (usesFormula && fml.OutputUnit) || BASIS_BY_VALUE[basis]?.unit || "ea").toLowerCase();
  const areas = roomAreasOf(room);
  let calculated = null, override = null, basisLabel = "", engine = null;

  if (method === "fixed") {
    calculated = element.FixedQty != null ? +element.FixedQty : null;
    basisLabel = "Fixed quantity";
  } else if (method === "placed") {
    calculated = placed;
    basisLabel = placed > 0 ? "Counted from the canvas" : "Nothing placed yet";
  } else if (usesFormula) {
    const v = computeRoomFormulaQty(fml, room, criteria || {}, ctx || {});
    calculated = v == null || !Number.isFinite(v) ? null : r1(v);
    basisLabel = `Formula ${fml.Code || ""}${fml.Expression ? ` = ${fml.Expression}` : ""}`.trim();
    if (calculated == null) {
      const why = explainRoomFormulaFailure(fml, room, criteria || {}, ctx || {});
      warnings.push({ code: "element_formula_unresolved", message: `Formula ${fml.Code || fml.FormulaID} did not resolve${fml.Expression ? ` (${fml.Expression})` : ""}${why ? ` — ${why}` : ""}. Check its variables under Setup ▸ Estimate Engine ▸ Formulas.` });
    }
  } else {
    switch (basis) {
      case "floor_area":   calculated = r1(areas.floorSF);     basisLabel = `Room floor area${room.PolygonJson ? " (traced outline)" : ""}`; break;
      case "ceiling_area": calculated = r1(areas.floorSF);     basisLabel = "Room ceiling area"; break;
      case "perimeter":    calculated = r1(areas.perimeterLF); basisLabel = "Room perimeter"; break;
      case "wall_area":    calculated = r1(areas.wallSF);      basisLabel = `Perimeter × ${r1(areas.H)} ft height`; break;
      case "engine_hepa": {
        const h = systems?.hvac?.hepa;
        engine = h || null;
        calculated = h ? h.calculatedQuantity : null;
        override = h?.overrideQuantity ?? null;
        basisLabel = h ? (h.governingConstraint === "coverage"
          ? `Calculated from ${r1(h.coveragePercent)}% ceiling coverage`
          : `Calculated from ${fmt0(h.requiredCfm)} CFM at ${fmt0(h.designAirflowPerUnit)} cfm/unit`) : "HEPA engine";
        if (h) warnings.push(...h.warnings);
        break;
      }
      case "engine_light": {
        const l = systems?.electrical?.lighting;
        engine = l || null;
        calculated = l ? (l.calculatedQuantity ?? l.fallbackQuantity ?? null) : null;
        override = l?.overrideQuantity ?? null;
        basisLabel = !l ? "Lighting engine"
          : l.calculatedQuantity != null ? `Calculated from ${l.targetFc} fc target${l.governingConstraint === "spacing" ? " (spacing governs)" : ""}`
          : l.fallbackQuantity != null ? `${l.quantitySource === "template" ? "Template" : "Project"} default — no fixture lumen data`
          : "No lighting basis — pick a light level and a fixture with lumen data";
        if (l) warnings.push(...l.warnings);
        break;
      }
      default:
        calculated = element.FixedQty != null ? +element.FixedQty : null;
        basisLabel = "Fixed quantity";
    }
  }

  // Back-outs (Takeoff Canvas 2.0 D-2): manual deduct polygons drawn against
  // THIS element (TakeoffConditionsDrawn.MeasureRole='deduct' +
  // AppliesToRoomElementID). Only an area basis resolved from the room's own
  // geometry is deducted: Gross − back-outs (inside the room, overlaps once)
  // = Calculated Net. The estimator's OverrideQty below still wins over the
  // net exactly as it won over the gross — one override store, unchanged.
  let net = null;
  if (!usesFormula && method === "calculated" && BACK_OUT_BASES.has(basis)) {
    const deducts = (backOuts ?? backOutsForElement(element, ctx?.takeoffConditions)).filter(d => d.points);
    if (deducts.length) {
      const ring = roomVertices(room);
      const n = isRing(ring) ? netScopeArea({ outer: ring, deducts }) : null;
      if (n?.ok) {
        const gross = areas.floorSF;
        net = {
          kind: "back_outs",
          gross: r1(gross),
          deductions: n.deductions.map(d => ({ ...d, sourceType: "back_out", area: r1(d.area), insideArea: r1(d.insideArea), outsideArea: r1(d.outsideArea) })),
          totalDeduction: r1(n.totalDeduction), overlapArea: r1(n.overlapArea),
          calculatedNet: r1(Math.max(0, gross - n.totalDeduction)),
        };
        calculated = net.calculatedNet;
        basisLabel = `${basisLabel} − ${deducts.length} back-out${deducts.length === 1 ? "" : "s"}`;
        warnings.push(...n.warnings);
      }
    }
  }

  // Opening deductions (Takeoff Canvas 2.0 D-3): a wall-area element loses
  // each opening's own width × height where that opening's policy says so —
  // the row's DeductionPolicy over the element's default
  // (SettingsJson.deductOpenings, false until turned on). Derived from the
  // opening's row every time, never stored; an unsized opening set to deduct
  // is reported and deducts nothing. Every opening in the room's walls is
  // listed (with its effective policy) so the card never shows only a total.
  if (!usesFormula && method === "calculated" && basis === "wall_area") {
    const rows = openings ?? openingsForRoom(room, ctx?.takeoffConditions, ctx?.rooms);
    if (rows.length) {
      const elementDefault = parseElementSettings(element).deductOpenings === true;
      const deductions = rows.map(o => {
        const policy = o.DeductionPolicy || "inherit";
        const deduct = resolveOpeningDeductionPolicy({ openingPolicy: policy, elementDefault });
        const area = openingAreaSf(o);
        return {
          id: o.TakeoffConditionDrawnID, sourceType: "opening",
          label: o.Mark || o.Code || o.Name || `Opening #${o.TakeoffConditionDrawnID}`,
          policy, effectivePolicy: deduct ? "deduct" : "keep",
          widthFt: openingWidthFt(o), heightFt: openingHeightFt(o),
          area: area != null ? r1(area) : null,
          insideArea: deduct && area != null ? r1(area) : 0,
          unsized: deduct && area == null,
        };
      });
      const total = r1(deductions.reduce((s, d) => s + d.insideArea, 0));
      const gross = areas.wallSF;
      net = { kind: "openings", gross: r1(gross), deductions, totalDeduction: total, overlapArea: 0, calculatedNet: r1(Math.max(0, gross - total)), elementDefault };
      const deducted = deductions.filter(d => d.effectivePolicy === "deduct" && !d.unsized).length;
      if (deducted) {
        calculated = net.calculatedNet;
        basisLabel = `${basisLabel} − ${deducted} opening${deducted === 1 ? "" : "s"}`;
      }
      for (const d of deductions.filter(d => d.unsized)) {
        warnings.push({ code: "opening_deduction_unsized", id: d.id, message: `Opening ${d.label} is set to deduct but has no width × height — nothing deducted. Set the type's default width / height or this instance's height.` });
      }
    }
  }

  if (!engineBasis) override = element.OverrideQty != null ? +element.OverrideQty : null;
  const effective = override ?? calculated ?? 0;
  if (override != null && calculated != null && override !== calculated && !engineBasis) {
    warnings.push({ code: "element_override_differs", message: `Manual override (${override}) differs from the ${method === "placed" ? "placed count" : "calculated quantity"} (${calculated}).` });
  }
  const required = effective;
  if (isPlaceable(element) && method !== "placed" && placed < required) {
    warnings.push({ code: "element_unplaced", message: `${required - placed} of ${required} not yet placed on the canvas.` });
  }
  return { basis, method, storedMethod, unit, calculated, override, effective, required, placed, basisLabel, warnings, engine, formula: fml, usesFormula, net };
}

// RoomElements.SettingsJson — element-specific choices, schemaless. Known
// keys: deductOpenings (bool, D-3 — a wall-area element's default policy for
// the openings in its room's walls). Unreadable → {}.
export function parseElementSettings(element) {
  try {
    const raw = element?.SettingsJson;
    if (raw == null || raw === "") return {};
    const v = typeof raw === "string" ? JSON.parse(raw) : raw;
    return v && typeof v === "object" && !Array.isArray(v) ? v : {};
  } catch {
    return {};
  }
}

// Index a revision's elements by RoomID for the engine-gating context
// (roomSystemsEngine.js reads ctx.roomElementsByRoom).
export function groupElementsByRoom(elements = []) {
  const map = {};
  for (const e of elements) (map[e.RoomID] ||= []).push(e);
  return map;
}

// ── Takeoff Canvas 2.0 Track I — "Why this quantity?" ───────────────────────
// One ordered, human-readable trace of a resolved element quantity, built
// only from what resolveElementQuantity already computed (no re-derivation):
// basis → formula / engine intermediates → Gross − each deduction = Net →
// override → effective → placed of required, plus the warnings. `sourceIds`
// are the drawn rows (back-outs, openings) behind the deductions so the
// canvas can highlight them. Shared by the Room Detail Flyout and the
// Estimate's element rows — one explanation everywhere.
const fmtQ = (v, unit) => v == null || !Number.isFinite(+v) ? "—" : `${(+v).toLocaleString(undefined, { maximumFractionDigits: 1 })} ${(unit || "ea").toUpperCase()}`;
export function quantityTrace(q, { element = null, room = null } = {}) {
  const unit = q?.unit || element?.UOM || "ea";
  const lines = [];
  const add = (label, value, note) => lines.push({ key: `${lines.length}:${label}`, label, value, note: note || null });
  if (!q) return { summary: "—", lines, sourceIds: [], warnings: [] };
  add("Basis", q.basisLabel || "—", q.usesFormula ? "per-room formula" : q.method === "placed" ? "count of placed symbols" : q.method === "fixed" ? "entered per room" : null);
  if (q.usesFormula && q.formula) add("Formula", `${q.formula.Code || ""} = ${q.formula.Expression || ""}`.trim());
  const h = q.basis === "engine_hepa" ? q.engine : null;
  if (h) {
    if (Number.isFinite(+h.requiredCfm)) add("Required airflow", `${Math.round(h.requiredCfm).toLocaleString()} CFM`, room ? `${room.RoomName || ""} floor × height × ACH ÷ 60` : null);
    if (Number.isFinite(+h.designAirflowPerUnit)) add("Design airflow per unit", `${Math.round(h.designAirflowPerUnit).toLocaleString()} CFM`, h.countByAirflow != null ? `→ ${h.countByAirflow} by airflow` : null);
    if (Number.isFinite(+h.coveragePercent) && h.countByCoverage != null) add("Coverage", `${h.coveragePercent}%`, `→ ${h.countByCoverage} by coverage`);
    if (h.governingConstraint) add("Governs", h.governingConstraint === "coverage" ? "ceiling coverage" : "airflow");
  }
  const l = q.basis === "engine_light" ? q.engine : null;
  if (l) {
    if (Number.isFinite(+l.targetFc)) add("Target illuminance", `${l.targetFc} fc`);
    if (Number.isFinite(+l.requiredLumens)) add("Required lumens", Math.round(l.requiredLumens).toLocaleString(), l.countByLumens != null ? `→ ${l.countByLumens} by lumens` : null);
    if (l.countBySpacing != null) add("Spacing check", `${l.countBySpacing} by spacing`);
    if (l.governingConstraint) add("Governs", l.governingConstraint === "spacing" ? "spacing" : "lumens");
  }
  const sourceIds = [];
  if (q.net) {
    add("Gross", fmtQ(q.net.gross, unit), q.net.kind === "openings" ? "perimeter × height" : "room area");
    for (const d of q.net.deductions || []) {
      if (d.id != null) sourceIds.push(d.id);
      if (d.sourceType === "opening") {
        if (d.unsized) add(d.label, "unsized", "set to deduct but has no width × height");
        else if (d.effectivePolicy === "deduct") add(`− ${d.label}`, `−${fmtQ(d.insideArea, unit)}`, `${d.widthFt} × ${d.heightFt} ft${d.policy !== "inherit" ? ` · ${d.policy === "deduct" ? "always deduct" : "never deduct"}` : ""}`);
        else add(d.label, "kept", d.policy === "do_not_deduct" ? "never deduct" : "element default: keep");
      } else if (d.skipped) {
        add(d.label, "skipped", "no area");
      } else {
        add(`− ${d.label}`, `−${fmtQ(d.insideArea, unit)}`, d.clipped ? `${fmtQ(d.outsideArea, unit).replace(/ SF$/, " SF")} outside the room — not deducted` : null);
      }
    }
    if (q.net.overlapArea > 0) add("Overlap", `${fmtQ(q.net.overlapArea, unit)}`, "overlapping back-outs deducted once");
    add("Calculated net", fmtQ(q.net.calculatedNet, unit));
  } else {
    add("Calculated", fmtQ(q.calculated, unit));
  }
  if (q.override != null) add("Override", fmtQ(q.override, unit), q.calculated != null && q.override !== q.calculated ? `differs from calculated ${fmtQ(q.calculated, unit)}` : null);
  add("Effective", fmtQ(q.effective, unit));
  if (element && isPlaceable(element) && q.method !== "placed") add("Placed", `${q.placed ?? 0} of ${q.required ?? 0}`, (q.placed ?? 0) < (q.required ?? 0) ? "not all placed on the canvas yet" : null);
  const warnings = (q.warnings || []).map(w => w.message);
  const summary = `${fmtQ(q.effective, unit)} — ${q.basisLabel || q.method}${q.override != null ? " · override" : ""}`;
  return { summary, lines, sourceIds, warnings };
}
